import { LitElement, html, nothing } from 'lit'
import { customElement, property } from 'lit/decorators.js'
import { consume } from '@lit/context'
import type { ComponentWidgetConfig, WidgetType } from 'components/shared/base-product-tile/base-product-tile-types.js'
import { style } from 'components/shared/product-tile-configurator/components/tile-configurator-widget-bar.style.js'
import { MobileModeContext, type MobileModeContextType } from 'components/shared/product-tile-configurator/context.js'

export interface WidgetDragStartDetail {
  type: WidgetType
  properties?: ComponentWidgetConfig
}

export interface WidgetLibraryItem {
  label: string
  type: WidgetType
  properties?: ComponentWidgetConfig
}

export interface WidgetLibraryGroup {
  label: string
  items: WidgetLibraryItem[]
}

export const WIDGET_LIBRARY_GROUPS: WidgetLibraryGroup[] = [
  {
    label: 'Content',
    items: [
      { label: 'Product Title', type: 'csc-product-tile-title' },
      { label: 'Brand Name', type: 'csc-brand' },
      { label: 'Product Code', type: 'csc-product-tile-product-code' },
      { label: 'Featured Attributes', type: 'csc-featured-attributes' },
    ],
  },
  {
    label: 'Indicators',
    items: [
      { label: 'Stock Indication', type: 'csc-stock-indication' },
      { label: 'Tags', type: 'csc-product-tile-tags' },
      { label: 'Variants', type: 'csc-product-tile-variants' },
      { label: 'Variant Indication', type: 'csc-product-tile-variant-indication' },
      { label: 'Rating', type: 'csc-product-rating' },
    ],
  },
  {
    label: 'Commerce',
    items: [
      { label: 'Price', type: 'csc-price' },
      { label: 'Suggested Retail Price', type: 'csc-retail-price' },
      { label: 'Levies', type: 'csc-levies' },
      { label: 'Order Form', type: 'csc-order-form' },
    ],
  },
  {
    label: 'Actions',
    items: [
      { label: 'Add to Order Template', type: 'csc-order-template-slide-in', properties: { triggerType: 'icon-button' } },
      { label: 'Request Quote', type: 'csc-quotation-add-product' },
      { label: 'View Product Button', type: 'csc-plp-cta-button' },
      { label: 'Compare', type: 'csc-compare-checkbox' },
    ]
  }
]

// The widget library, always visible in the configurator's left panel — groups stacked
// vertically. All selection-driven settings (area, zone, widget) live in the right panel.
@customElement('csc-tile-configurator-widget-bar')
export class CSCTileConfiguratorWidgetBar extends LitElement {
  static override styles = [style]

  @consume({ context: MobileModeContext, subscribe: true })
  private mobileMode!: MobileModeContextType

  // How many of each widget type are placed on the currently visible canvas — set by
  // csc-product-tile-configurator, which owns which canvas (grid/list) is active.
  @property({ attribute: false })
  placedCounts: Partial<Record<WidgetType, number>> = {}

  private handleDragStart(event: DragEvent, type: WidgetType, properties?: ComponentWidgetConfig) {
    if (event.dataTransfer) {
      event.dataTransfer.setData('application/json', JSON.stringify({ type, properties }))
      event.dataTransfer.effectAllowed = 'move'
    }
    const target = event.currentTarget as HTMLElement
    target?.classList.add('dragging')
    this.dispatchEvent(new CustomEvent<WidgetDragStartDetail>('widget-drag-start', {
      detail: { type, ...(properties && { properties }) },
      bubbles: true,
      composed: true,
    }))
  }

  private handleDragEnd(event: DragEvent) {
    (event.currentTarget as HTMLElement).classList.remove('dragging')
    this.dispatchEvent(new CustomEvent('widget-drag-end', { bubbles: true, composed: true }))
  }

  private renderPlacedCount(type: WidgetType) {
    const count = this.placedCounts[type] ?? 0
    if (!count) return nothing
    return html`
      <span class="widget-item-count" title="On the canvas ${count}×" aria-label="On the canvas ${count} ${count === 1 ? 'time' : 'times'}">
        <cse-text variant="caption-s">${count}</cse-text>
      </span>
    `
  }

  override render() {
    // View-only mobile preview disables adding widgets at all, same as the canvas itself
    // becoming fully inert (there's nothing mobile-specific to place them into — see
    // MobileModeContextType) — dragging is turned off at the element (not just the listener)
    // so there's no native drag ghost to suggest otherwise.
    const disabled = this.mobileMode
    return html`
      <div class="widget-bar ${disabled ? 'disabled' : ''}">
        ${WIDGET_LIBRARY_GROUPS.map(group => html`
          <div class="widget-bar-group">
            <cse-text variant="caption-s">${group.label}</cse-text>
            <div class="widget-bar-items">
              ${group.items.map(item => html`
                <div
                  class="widget-item"
                  draggable="${disabled ? 'false' : 'true'}"
                  aria-disabled="${disabled ? 'true' : 'false'}"
                  @dragstart=${disabled ? undefined : (e: DragEvent) => this.handleDragStart(e, item.type, item.properties)}
                  @dragend=${disabled ? undefined : this.handleDragEnd}>
                  <cse-icon icon-name="grip-dots-vertical" icon-style="solid"></cse-icon>
                  <cse-text variant="body-s">${item.label}</cse-text>
                  ${this.renderPlacedCount(item.type)}
                </div>
              `)}
            </div>
          </div>
        `)}
      </div>
    `
  }
}

declare global {
  interface HTMLElementTagNameMap {
    'csc-tile-configurator-widget-bar': CSCTileConfiguratorWidgetBar
  }
}
